import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { AlertCircle, Bot, Brain, Check, LoaderCircle, Lock, MessageSquarePlus, Pencil, Send, Sparkles, X } from 'lucide-react';
import useSWR from 'swr';
import api from '@/api/engine';
import type {
  AnswerMode,
  CodexAffirmationOutcome,
  ConfidenceInOwnAccount,
  ResponseRelationToEpisode,
} from '@/api/types';
import {
  classifyEngineRequestFailure,
  engineFetchJson,
  EngineHttpError,
  streamEngineChat,
  type EngineStreamEvent,
} from '@/lib/engineHttp';
import { useEnabledModels } from '@/components/settings/panels/useEnabledModels';
import { fetchLlmPriceCatalog, type LlmPriceCatalogEntry } from '@/api/pricing';
import {
  buildAffirmationPayload,
  crystallizedCountFrom,
  EMPTY_REASON_DRAFT,
  interpretCodexContext,
  memoryHeaderLabel,
  type CodexRecall,
  type ReasonDraft,
  type RecalledChunk,
} from './chatSeityLegibility';

interface ChatConversation {
  thread_id: string;
  title: string;
  last_message_preview: string | null;
  unread_count: number;
  model_used: string | null;
  created_at: string;
  last_message_at: string | null;
  workspace_id: string | null;
}

interface ChatModel {
  id: string;
  display_name: string;
  provider: string;
}

interface ServerChatMessage {
  id: string;
  thread_id: string;
  author_type: 'human' | 'agent' | 'alter_ego';
  content_text: string;
  sent_at: string | null;
  model_runtime?: string | null;
  sender_display_name?: string | null;
  metadata_json?: {
    chat?: {
      cost?: {
        total_tokens?: number;
        model_id?: string;
      };
    };
  } | null;
}

interface UiChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sender: string;
  sentAt: string | null;
  modelId: string | null;
  totalTokens: number | null;
  streaming?: boolean;
  pending?: boolean;
  // Seity legibility (SEITY-CONVERSATION-E2E-1) — makes the read/write memory
  // loop visible on the conversation surface.
  memoryCount?: number; // Codex chunks recalled into this reply.
  recallSources?: string[]; // Their sources, for a tooltip.
  recallChunks?: RecalledChunk[]; // The recalled chunks, so each can be affirmed/corrected/rejected.
  memoryLocked?: boolean; // Seity was locked this turn — no recall was possible.
  crystallizedCount?: number; // New memories crystallized after this turn.
}

type ConnectionState = 'connecting' | 'connected' | 'offline';

const THREAD_STORAGE_KEY = 'seitiate-plus.chat.active-thread';

function assistantName(personaName: string | null, fallback?: string | null): string {
  return personaName?.trim() || fallback?.trim() || 'your assistant';
}

function deriveTitle(input: string): string {
  const trimmed = input.trim().replace(/\s+/g, ' ');
  if (!trimmed) return 'Untitled chat';
  return trimmed.length > 42 ? `${trimmed.slice(0, 42)}...` : trimmed;
}

function formatTimestamp(value: string | null): string {
  if (!value) return 'just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'just now';
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function summarizeConversation(conversation: ChatConversation): string {
  if (conversation.last_message_preview?.trim()) return conversation.last_message_preview;
  return conversation.model_used ? `Last model: ${conversation.model_used}` : 'New conversation';
}

function normalizeModelId(modelId: string): string {
  return modelId
    .trim()
    .toLowerCase()
    .replace(/[\s_.:]+/g, '-')
    .replace(/-latest$/, '')
    .replace(/-001$/, '');
}

/**
 * SWR key for the LLM price-catalog read (Seitiate+ backend). The picker only
 * borrows each row's display_name; a model with no catalog row renders its
 * engine model_id. Nothing about the picker is bundled with the app — the
 * hand-kept front-end catalog (llmPricing.ts) was removed under
 * DOCKET-2026-10-20-05 after it drifted from the engine.
 */
const LLM_CATALOG_SWR_KEY = '/api/v1/pricing/catalog?resource_type=llm';

function displayNamesByKey(entries: LlmPriceCatalogEntry[] | undefined): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of entries ?? []) {
    map.set(`${entry.provider}::${normalizeModelId(entry.model)}`, entry.display_name ?? entry.model);
  }
  return map;
}

function mapServerMessage(message: ServerChatMessage, personaName: string | null): UiChatMessage {
  const cost = message.metadata_json?.chat?.cost;
  return {
    id: message.id,
    role: message.author_type === 'human' ? 'user' : 'assistant',
    content: message.content_text,
    sender:
      message.author_type === 'human'
        ? 'You'
        : assistantName(personaName, message.sender_display_name),
    sentAt: message.sent_at,
    modelId: message.model_runtime || cost?.model_id || null,
    totalTokens: typeof cost?.total_tokens === 'number' ? cost.total_tokens : null,
  };
}

function ensureDraftMessage(
  messages: UiChatMessage[],
  draftId: string,
  personaName: string | null,
  modelId: string | null,
): UiChatMessage[] {
  if (messages.some((message) => message.id === draftId)) {
    return messages;
  }
  return [
    ...messages,
    {
      id: draftId,
      role: 'assistant',
      content: '',
      sender: assistantName(personaName),
      sentAt: new Date().toISOString(),
      modelId,
      totalTokens: null,
      streaming: true,
    },
  ];
}

function appendDraftDelta(
  messages: UiChatMessage[],
  draftId: string,
  delta: string,
  personaName: string | null,
  modelId: string | null,
): UiChatMessage[] {
  const existing = messages.find((message) => message.id === draftId);
  if (!existing) {
    return ensureDraftMessage(messages, draftId, personaName, modelId).map((message) =>
      message.id === draftId ? { ...message, content: delta } : message,
    );
  }

  return messages.map((message) =>
    message.id === draftId
      ? {
          ...message,
          content: `${message.content}${delta}`,
          sender: assistantName(personaName, message.sender),
          modelId: message.modelId || modelId,
          streaming: true,
        }
      : message,
  );
}

// Seity Wave 1 W1.4 — the person may affirm, correct, or reject a memory the
// seity recalled into a reply, one chunk at a time (the engine route is
// per-chunk). Each choice sends exactly one affirmation event; every reason
// field is optional and left null when blank. Taking no action sends nothing.
const OUTCOME_LABELS: Record<CodexAffirmationOutcome, string> = {
  affirmed: 'Affirm',
  corrected: 'Correct',
  rejected: 'Reject',
};
const RELATION_OPTIONS: ResponseRelationToEpisode[] = ['contemporaneous', 'retrospective', 'hypothetical'];
const CONFIDENCE_OPTIONS: ConfidenceInOwnAccount[] = ['certain', 'fairly_sure', 'unsure'];
const ANSWER_MODE_OPTIONS: AnswerMode[] = ['explanation', 'correction', 'preference', 'speculation', 'refusal'];

// `episodeId` is the work episode the recall belongs to, when one is known
// (episodes are compiled by B2, wave 2). The chat surface knows none today, so
// it passes nothing and `episode_id` stays null — a thread id is not an episode.
export function RecallAffirmation({ chunks, episodeId }: { chunks: RecalledChunk[]; episodeId?: string | null }) {
  const [open, setOpen] = useState(false);
  const [recorded, setRecorded] = useState<Record<string, CodexAffirmationOutcome>>({});
  return (
    <div style={{ marginTop: 6, fontSize: 11 }}>
      <button
        type="button"
        className="mono"
        onClick={() => setOpen((value) => !value)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--sp-text-dim)', fontSize: 10 }}
      >
        {open ? 'hide recalled memories' : 'review recalled memories'}
      </button>
      {/* Keep rows mounted: disclosure must not discard an in-flight mutation. */}
        <ul hidden={!open} style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, display: open ? 'grid' : 'none', gap: 6 }}>
          {chunks.map((chunk) => (
            <RecalledChunkRow
              key={chunk.id}
              chunk={chunk}
              episodeId={episodeId ?? null}
              recordedOutcome={recorded[chunk.id] ?? null}
              onRecorded={(outcome) => setRecorded((previous) => ({ ...previous, [chunk.id]: outcome }))}
            />
          ))}
        </ul>
    </div>
  );
}

function RecalledChunkRow({
  chunk,
  episodeId,
  recordedOutcome,
  onRecorded,
}: {
  chunk: RecalledChunk;
  episodeId: string | null;
  recordedOutcome: CodexAffirmationOutcome | null;
  onRecorded: (outcome: CodexAffirmationOutcome) => void;
}) {
  const [outcome, setOutcome] = useState<CodexAffirmationOutcome | null>(null);
  const [draft, setDraft] = useState<ReasonDraft>(EMPTY_REASON_DRAFT);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const payload = buildAffirmationPayload(outcome, { ...draft, episodeId: episodeId ?? '' });

  const choose = (next: CodexAffirmationOutcome) => {
    setOutcome(next);
    // Corrected text starts blank: the preview is truncated, so pre-filling it
    // would risk writing a clipped memory back as the correction.
    setDraft(EMPTY_REASON_DRAFT);
    setError(null);
  };
  const cancel = () => {
    setOutcome(null);
    setDraft(EMPTY_REASON_DRAFT);
    setError(null);
  };
  const send = async () => {
    if (!payload || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.codex.affirmChunk(chunk.id, payload);
      onRecorded(payload.outcome);
      cancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record this.');
    } finally {
      setSending(false);
    }
  };
  const field = (key: keyof ReasonDraft) => ({
    value: draft[key],
    onChange: (event: { target: { value: string } }) =>
      setDraft((previous) => ({ ...previous, [key]: event.target.value })),
  });

  return (
    <li className="sp-widget" style={{ padding: 8 }}>
      <div style={{ color: 'var(--sp-text)', whiteSpace: 'pre-wrap' }}>
        {chunk.contentPreview || <span style={{ color: 'var(--sp-text-dim)' }}>(no preview)</span>}
      </div>
      <div className="mono" style={{ marginTop: 4, fontSize: 10, color: 'var(--sp-text-dim)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {chunk.source ? <span>{chunk.source}</span> : null}
        {recordedOutcome ? (
          <span style={{ color: 'var(--sp-ok)' }}>recorded: {recordedOutcome}</span>
        ) : null}
        {outcome === null
          ? (Object.keys(OUTCOME_LABELS) as CodexAffirmationOutcome[]).map((option) => (
              <button key={option} type="button" className="sp-btn" style={{ padding: '2px 8px', fontSize: 10 }} onClick={() => choose(option)}>
                {option === 'affirmed' ? <Check size={10} /> : option === 'corrected' ? <Pencil size={10} /> : <X size={10} />}
                {OUTCOME_LABELS[option]}
              </button>
            ))
          : null}
      </div>
      {outcome !== null ? (
        <div style={{ marginTop: 6, display: 'grid', gap: 6 }}>
          {outcome === 'corrected' ? (
            <label style={{ display: 'grid', gap: 2 }}>
              <span>What it should say</span>
              <textarea className="sp-input" rows={2} {...field('correctedContent')} />
            </label>
          ) : null}
          <label style={{ display: 'grid', gap: 2 }}>
            <span>Why, in a sentence (optional)</span>
            <input className="sp-input" {...field('rationale')} />
          </label>
          <label style={{ display: 'grid', gap: 2 }}>
            <span>Except when… (optional)</span>
            <input className="sp-input" {...field('exceptionScope')} />
          </label>
          <details>
            <summary style={{ cursor: 'pointer', color: 'var(--sp-text-dim)' }}>more about your answer (optional)</summary>
            <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
              <label style={{ display: 'grid', gap: 2 }}>
                <span>Relation to what happened</span>
                <select className="sp-input" {...field('responseRelationToEpisode')}>
                  <option value="">not said</option>
                  {RELATION_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 2 }}>
                <span>How sure you are</span>
                <select className="sp-input" {...field('confidenceInOwnAccount')}>
                  <option value="">not said</option>
                  {CONFIDENCE_OPTIONS.map((option) => <option key={option} value={option}>{option.replace('_', ' ')}</option>)}
                </select>
              </label>
              <label style={{ display: 'grid', gap: 2 }}>
                <span>Kind of answer</span>
                <select className="sp-input" {...field('answerMode')}>
                  <option value="">not said</option>
                  {ANSWER_MODE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </label>
            </div>
          </details>
          {error ? <div style={{ color: 'var(--sp-error)' }}>{error}</div> : null}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="sp-btn sp-btn-primary" disabled={!payload || sending} onClick={() => void send()}>
              {sending ? 'Recording…' : `${OUTCOME_LABELS[outcome]} this memory`}
            </button>
            <button type="button" className="sp-btn" disabled={sending} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function replaceMessage(messages: UiChatMessage[], messageId: string, replacement: UiChatMessage): UiChatMessage[] {
  const exists = messages.some((message) => message.id === messageId);
  if (!exists) return [...messages, replacement];
  return messages.map((message) => (message.id === messageId ? replacement : message));
}

export function ChatPage() {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [messages, setMessages] = useState<UiChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [personaName, setPersonaName] = useState<string | null>(null);
  // Seity legibility: the tenant's total Codex size (header), and whether the
  // seity re-locked mid-session (1h TDK TTL / engine restart) so the founder
  // gets a re-unlock affordance instead of a silently amnesiac chatbot.
  const [codexTotal, setCodexTotal] = useState<number | null>(null);
  const [seityLocked, setSeityLocked] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const engineReachableRef = useRef(false);
  const { enabled: enabledModels } = useEnabledModels();
  const { data: catalogEntries } = useSWR<LlmPriceCatalogEntry[]>(
    LLM_CATALOG_SWR_KEY,
    () => fetchLlmPriceCatalog(),
    // A label nicety, not a critical read: no retry storm, and a failure just
    // falls back to raw engine model ids in the picker.
    { shouldRetryOnError: false },
  );

  const modelOptions = useMemo(() => {
    const labels = displayNamesByKey(catalogEntries);
    const seen = new Set<string>();
    return enabledModels
      .map((entry) => {
        const key = `${entry.provider_id}::${normalizeModelId(entry.model_id)}`;
        const model: ChatModel = {
          id: entry.model_id,
          display_name: labels.get(key) ?? entry.model_id,
          provider: entry.provider_id,
        };
        return { key, model };
      })
      .filter(({ key }) => {
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map(({ model }) => model);
  }, [enabledModels, catalogEntries]);

  const markEngineReachable = useCallback(() => {
    engineReachableRef.current = true;
    setConnectionState('connected');
  }, []);

  const updateConnectionStateFromError = useCallback((error: unknown) => {
    const classification = classifyEngineRequestFailure(error);

    if (classification.kind === 'server') {
      engineReachableRef.current = true;
      setConnectionState('connected');
      return;
    }

    if (classification.kind === 'transport') {
      setConnectionState('offline');
      return;
    }

    // Once the engine has answered in this tab, later ambiguous fetch TypeErrors
    // should not demote the header into an "offline" state.
    if (classification.kind === 'ambiguous-transport' && engineReachableRef.current) {
      setConnectionState('connected');
      return;
    }

    if (classification.kind !== 'abort') {
      setConnectionState('connected');
    }
  }, []);

  const loadThread = useCallback(async (threadId: string, nextPersonaName?: string | null) => {
    setLoadingMessages(true);
    setErrorMessage(null);
    try {
      const serverMessages = await engineFetchJson<ServerChatMessage[]>(
        `/chat/conversations/${threadId}/messages`,
      );
      setMessages(serverMessages.map((message) => mapServerMessage(message, nextPersonaName ?? personaName)));
      setActiveThreadId(threadId);
      localStorage.setItem(THREAD_STORAGE_KEY, threadId);
      markEngineReachable();
    } catch (error) {
      const detail = error instanceof Error ? error.message : 'Failed to load chat history';
      setErrorMessage(detail);
      updateConnectionStateFromError(error);
    } finally {
      setLoadingMessages(false);
    }
  }, [markEngineReachable, personaName, updateConnectionStateFromError]);

  const refreshConversations = useCallback(async (): Promise<ChatConversation[]> => {
    const data = await engineFetchJson<ChatConversation[]>('/chat/conversations');
    setConversations(data);
    return data;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setConnectionState('connecting');
      try {
        const [settingsResult, conversationsResult] = await Promise.all([
          api.settings.getAll(),
          engineFetchJson<ChatConversation[]>('/chat/conversations'),
        ]);

        if (cancelled) return;

        setPersonaName(settingsResult.persona_name || null);
        setConversations(conversationsResult);
        markEngineReachable();

        const persistedThreadId = localStorage.getItem(THREAD_STORAGE_KEY);
        const initialThreadId =
          persistedThreadId && conversationsResult.some((conversation) => conversation.thread_id === persistedThreadId)
            ? persistedThreadId
            : conversationsResult[0]?.thread_id ?? null;

        if (initialThreadId) {
          await loadThread(initialThreadId, settingsResult.persona_name || null);
        } else {
          setMessages([]);
          setActiveThreadId(null);
        }
      } catch (error) {
        if (cancelled) return;
        setErrorMessage(error instanceof Error ? error.message : 'Engine unreachable');
        updateConnectionStateFromError(error);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadThread, markEngineReachable, updateConnectionStateFromError]);

  useEffect(() => {
    if (!selectedModel) return;
    if (modelOptions.some((model) => model.id === selectedModel)) return;
    setSelectedModel('');
  }, [modelOptions, selectedModel]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, streaming]);

  const statusLabel = useMemo(() => {
    if (connectionState === 'offline') return 'engine offline';
    if (connectionState === 'connecting') return 'connecting to engine';
    return streaming ? 'streaming reply' : 'engine connected';
  }, [connectionState, streaming]);

  const startNewSession = useCallback(() => {
    setActiveThreadId(null);
    localStorage.removeItem(THREAD_STORAGE_KEY);
    setMessages([]);
    setErrorMessage(null);
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || streaming) return;

    const pendingUserId = `pending-user-${Date.now()}`;
    const draftAssistantId = `draft-assistant-${Date.now()}`;
    let resolvedThreadId = activeThreadId;
    // Turn-scoped seity state, carried from the codex_context event (which
    // arrives on the draft) onto the finalized assistant message, and used to
    // target the post-turn crystallization chip.
    let turnRecall: CodexRecall | null = null;
    let lastAssistantId: string | null = null;

    setInput('');
    setErrorMessage(null);
    setConnectionState('connected');
    setStreaming(true);
    setMessages((previous) => [
      ...previous,
      {
        id: pendingUserId,
        role: 'user',
        content,
        sender: 'You',
        sentAt: new Date().toISOString(),
        modelId: null,
        totalTokens: null,
        pending: true,
      },
    ]);

    const handleEvent = (streamEvent: EngineStreamEvent) => {
      const payload = streamEvent.data as Record<string, unknown>;
      markEngineReachable();

      if (streamEvent.event === 'message_created' && payload.message) {
        const message = payload.message as unknown as ServerChatMessage;
        setMessages((previous) =>
          replaceMessage(
            previous,
            pendingUserId,
            mapServerMessage(message, personaName),
          ),
        );
        return;
      }

      if (streamEvent.event === 'thread_id' && typeof payload.thread_id === 'string') {
        const nextThreadId = payload.thread_id;
        resolvedThreadId = nextThreadId;
        setActiveThreadId(nextThreadId);
        localStorage.setItem(THREAD_STORAGE_KEY, nextThreadId);
        setConversations((previous) => {
          const withoutCurrent = previous.filter((conversation) => conversation.thread_id !== nextThreadId);
          return [
            {
              thread_id: nextThreadId,
              title: deriveTitle(content),
              last_message_preview: content,
              unread_count: 0,
              model_used: selectedModel || null,
              created_at: new Date().toISOString(),
              last_message_at: new Date().toISOString(),
              workspace_id: null,
            },
            ...withoutCurrent,
          ];
        });
        return;
      }

      if (streamEvent.event === 'agent_thinking') {
        const modelId = typeof payload.model === 'string' ? payload.model : selectedModel || null;
        setMessages((previous) => ensureDraftMessage(previous, draftAssistantId, personaName, modelId));
        return;
      }

      if (streamEvent.event === 'codex_context') {
        const recall = interpretCodexContext(payload);
        turnRecall = recall;
        if (recall.codexTotal !== null) setCodexTotal(recall.codexTotal);
        if (recall.locked) setSeityLocked(true);
        // Reflect on the streaming draft so recall is visible immediately.
        setMessages((previous) =>
          previous.map((message) =>
            message.id === draftAssistantId
              ? {
                  ...message,
                  memoryCount: recall.memoryCount,
                  recallSources: recall.sources,
                  recallChunks: recall.chunks,
                  memoryLocked: recall.locked,
                }
              : message,
          ),
        );
        return;
      }

      if (streamEvent.event === 'memory_crystallized') {
        const count = crystallizedCountFrom(payload);
        // A successful write proves the seity is unlocked again.
        setSeityLocked(false);
        if (count > 0 && lastAssistantId) {
          const targetId = lastAssistantId;
          setMessages((previous) =>
            previous.map((message) =>
              message.id === targetId ? { ...message, crystallizedCount: count } : message,
            ),
          );
        }
        return;
      }

      if (streamEvent.event === 'memory_locked') {
        setSeityLocked(true);
        return;
      }

      if (streamEvent.event === 'agent_response' && typeof payload.delta === 'string') {
        setMessages((previous) =>
          appendDraftDelta(
            previous,
            draftAssistantId,
            payload.delta as string,
            personaName,
            selectedModel || null,
          ),
        );
        return;
      }

      if (streamEvent.event === 'cost') {
        const modelId = typeof payload.model_id === 'string' ? payload.model_id : null;
        const totalTokens =
          typeof payload.total_tokens === 'number' ? (payload.total_tokens as number) : null;
        setMessages((previous) =>
          previous.map((message) =>
            message.id === draftAssistantId
              ? {
                  ...message,
                  modelId: modelId || message.modelId,
                  totalTokens: totalTokens ?? message.totalTokens,
                }
              : message,
          ),
        );
        return;
      }

      if (streamEvent.event === 'agent_message' && payload.message) {
        const message = payload.message as unknown as ServerChatMessage;
        const replacement = mapServerMessage(message, personaName);
        // Carry the turn's recall metadata (from codex_context) onto the
        // finalized message, and remember its id so the post-turn
        // crystallization chip can attach to it.
        if (turnRecall) {
          replacement.memoryCount = turnRecall.memoryCount;
          replacement.recallSources = turnRecall.sources;
          replacement.recallChunks = turnRecall.chunks;
          replacement.memoryLocked = turnRecall.locked;
        }
        lastAssistantId = replacement.id;
        setMessages((previous) => {
          const withoutDraft = previous.filter((entry) => entry.id !== draftAssistantId);
          return [...withoutDraft, replacement];
        });
        return;
      }

      if (streamEvent.event === 'error') {
        setErrorMessage(
          typeof payload.detail === 'string' ? payload.detail : 'The engine returned an error',
        );
        // Don't flip to `offline` — the engine was reachable enough to stream
        // an error event. The request failed, the connection didn't.
      }
    };

    try {
      await streamEngineChat({
        content,
        threadId: activeThreadId,
        modelOverride: selectedModel || undefined,
        onEvent: handleEvent,
      });

      markEngineReachable();
      const refreshed = await refreshConversations();
      markEngineReachable();
      if (resolvedThreadId) {
        const currentConversation = refreshed.find(
          (conversation) => conversation.thread_id === resolvedThreadId,
        );
        if (currentConversation) {
          setConversations((previous) => [
            currentConversation,
            ...previous.filter((conversation) => conversation.thread_id !== resolvedThreadId),
          ]);
        }
      }
    } catch (error) {
      const detail =
        error instanceof EngineHttpError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Send failed';
      setErrorMessage(detail);
      updateConnectionStateFromError(error);
      setMessages((previous) =>
        previous
          .map((message) => (message.id === pendingUserId ? { ...message, pending: false } : message))
          .filter((message) => message.id !== draftAssistantId || message.content.trim().length > 0),
      );
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '280px minmax(0, 1fr)',
        gap: 'var(--sp-space-4)',
        height: 'calc(100vh - 76px)',
      }}
    >
      <aside
        style={{
          border: '1px solid var(--sp-border)',
          borderRadius: 'var(--sp-radius-lg)',
          background: 'var(--sp-bg-elev)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--sp-space-3)',
            borderBottom: '1px solid var(--sp-border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-space-2)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Conversations</span>
            <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
              {conversations.length}
            </span>
          </div>
          <button
            type="button"
            className="sp-btn"
            onClick={startNewSession}
            style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}
          >
            <MessageSquarePlus size={14} />
            New Session
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: 'var(--sp-space-2)' }}>
          {conversations.length === 0 ? (
            <div className="sp-empty" style={{ padding: '24px 12px', marginTop: 8 }}>
              No saved conversations yet.
            </div>
          ) : (
            conversations.map((conversation) => {
              const active = conversation.thread_id === activeThreadId;
              return (
                <button
                  key={conversation.thread_id}
                  type="button"
                  onClick={() => void loadThread(conversation.thread_id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 'var(--sp-radius-lg)',
                    border: active
                      ? '1px solid rgba(127, 209, 185, 0.35)'
                      : '1px solid transparent',
                    background: active ? 'rgba(127, 209, 185, 0.08)' : 'transparent',
                    color: 'var(--sp-text)',
                    cursor: 'pointer',
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {conversation.title}
                    </div>
                    {conversation.unread_count > 0 ? (
                      <span
                        className="mono"
                        style={{
                          fontSize: 10,
                          color: 'var(--sp-accent)',
                          minWidth: 16,
                          textAlign: 'right',
                        }}
                      >
                        {conversation.unread_count}
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      color: 'var(--sp-text-muted)',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {summarizeConversation(conversation)}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      <section
        style={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--sp-border)',
          borderRadius: 'var(--sp-radius-lg)',
          background: 'var(--sp-bg-elev)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--sp-space-3) var(--sp-space-4)',
            borderBottom: '1px solid var(--sp-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--sp-space-3)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-space-3)' }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                background: 'rgba(127, 209, 185, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--sp-accent)',
              }}
            >
              <Bot size={16} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {assistantName(personaName)}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: 'var(--sp-text-dim)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {memoryHeaderLabel(codexTotal) ? (
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--sp-accent)' }}
                    title="Your seity's accumulated memory (Codex)"
                  >
                    <Brain size={11} />
                    {memoryHeaderLabel(codexTotal)}
                  </span>
                ) : (
                  <span>{statusLabel}</span>
                )}
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: connectionState === 'offline' ? 'var(--sp-error)' : 'var(--sp-text-dim)',
              fontSize: 11,
            }}
          >
            {connectionState === 'offline' ? <AlertCircle size={14} /> : null}
            {connectionState === 'connecting' ? <LoaderCircle size={14} className="animate-spin" /> : null}
            <span className="mono">{statusLabel}</span>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-space-4)' }}>
          {loadingMessages ? (
            <div className="sp-empty" style={{ marginTop: 24 }}>
              Loading conversation history...
            </div>
          ) : messages.length === 0 ? (
            <div className="sp-empty" style={{ marginTop: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                Start a conversation
              </div>
              <div style={{ color: 'var(--sp-text-muted)', fontSize: 13 }}>
                Messages persist in the engine. Pick a model and send the first message.
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 'var(--sp-space-3)',
                }}
              >
                <div
                  style={{
                    maxWidth: '78%',
                    background:
                      message.role === 'assistant' ? 'var(--sp-bg-elev-2)' : 'rgba(127, 209, 185, 0.1)',
                    border: `1px solid ${
                      message.role === 'assistant'
                        ? 'var(--sp-border)'
                        : 'rgba(127, 209, 185, 0.25)'
                    }`,
                    borderRadius: 'var(--sp-radius-lg)',
                    padding: 'var(--sp-space-3) var(--sp-space-4)',
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      marginBottom: 6,
                    }}
                  >
                    <span
                      className="mono"
                      style={{
                        fontSize: 10,
                        color:
                          message.role === 'assistant'
                            ? 'var(--sp-accent)'
                            : 'var(--sp-text-dim)',
                      }}
                    >
                      {message.sender}
                    </span>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--sp-text-dim)' }}>
                      {formatTimestamp(message.sentAt)}
                    </span>
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {message.content || (message.streaming ? '...' : '')}
                  </div>
                  {(message.modelId || message.totalTokens || message.streaming || message.pending) ? (
                    <div
                      className="mono"
                      style={{
                        marginTop: 8,
                        fontSize: 10,
                        color: 'var(--sp-text-dim)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                    >
                      {message.modelId ? <span>{message.modelId}</span> : null}
                      {typeof message.totalTokens === 'number' ? (
                        <span>{message.totalTokens} tokens</span>
                      ) : null}
                      {message.streaming ? <span>streaming...</span> : null}
                      {message.pending ? <span>pending...</span> : null}
                    </div>
                  ) : null}
                  {message.role === 'assistant' &&
                  (message.memoryLocked ||
                    (message.memoryCount ?? 0) > 0 ||
                    (message.crystallizedCount ?? 0) > 0) ? (
                    <div
                      className="mono"
                      style={{
                        marginTop: 6,
                        fontSize: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        flexWrap: 'wrap',
                      }}
                    >
                      {message.memoryLocked ? (
                        <span
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--sp-warn)' }}
                          title="Your seity was locked this turn, so it could not recall past context."
                        >
                          <Lock size={11} />
                          memory locked — no recall
                        </span>
                      ) : (message.memoryCount ?? 0) > 0 ? (
                        <span
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--sp-accent)' }}
                          title={
                            message.recallSources && message.recallSources.length > 0
                              ? `From your Codex: ${message.recallSources.join(', ')}`
                              : 'Recalled from your Codex'
                          }
                        >
                          <Brain size={11} />
                          recalled {message.memoryCount}{' '}
                          {message.memoryCount === 1 ? 'memory' : 'memories'}
                        </span>
                      ) : null}
                      {(message.crystallizedCount ?? 0) > 0 ? (
                        <span
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--sp-accent)' }}
                          title="Your seity crystallized new memory from this exchange."
                        >
                          <Sparkles size={11} />
                          remembered {message.crystallizedCount}{' '}
                          {message.crystallizedCount === 1 ? 'thing' : 'things'}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {message.role === 'assistant' &&
                  !message.streaming &&
                  !message.memoryLocked &&
                  (message.recallChunks?.length ?? 0) > 0 ? (
                    <RecallAffirmation chunks={message.recallChunks ?? []} />
                  ) : null}
                </div>
              </div>
            ))
          )}
          <div ref={endRef} />
        </div>

        {seityLocked ? (
          <div
            style={{
              borderTop: '1px solid var(--sp-warn, rgba(224, 180, 104, 0.35))',
              background: 'rgba(224, 180, 104, 0.08)',
              color: 'var(--sp-warn, #d8a24a)',
              padding: '10px var(--sp-space-4)',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Lock size={14} />
            <span style={{ flex: 1 }}>
              Your seity locked (the unlock session timed out), so it can't recall or remember
              this conversation. Unlock it to keep its memory growing.
            </span>
            <button
              type="button"
              className="sp-btn"
              onClick={() => window.location.reload()}
              title="Reload to unlock your seity"
            >
              Unlock
            </button>
          </div>
        ) : null}

        {errorMessage ? (
          <div
            style={{
              borderTop: '1px solid rgba(224, 104, 104, 0.25)',
              background: 'rgba(224, 104, 104, 0.08)',
              color: 'var(--sp-error)',
              padding: '10px var(--sp-space-4)',
              fontSize: 12,
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <form
          onSubmit={onSubmit}
          style={{
            padding: 'var(--sp-space-3) var(--sp-space-4)',
            borderTop: '1px solid var(--sp-border)',
            display: 'grid',
            gridTemplateColumns: 'minmax(0,1fr) 220px auto',
            gap: 'var(--sp-space-2)',
            alignItems: 'center',
          }}
        >
          <input
            className="sp-input"
            type="text"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={`Message ${assistantName(personaName)}...`}
            disabled={streaming}
          />
          <select
            className="sp-input"
            value={selectedModel}
            onChange={(event) => setSelectedModel(event.target.value)}
            disabled={streaming || modelOptions.length === 0}
          >
            <option value="">Auto (engine picks)</option>
            {modelOptions.map((model) => (
              <option key={model.id} value={model.id}>
                {model.display_name} ({model.provider})
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="sp-btn sp-btn-primary"
            disabled={!input.trim() || streaming}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px' }}
          >
            {streaming ? <LoaderCircle size={14} className="animate-spin" /> : <Send size={14} />}
            Send
          </button>
        </form>
      </section>
    </div>
  );
}
